import type { Graphics } from 'pixi.js';
import { lerpColor } from '../../color';
import type { Body, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const ROPE_V = 7;
const KNOTS = 8;
const SEQUINS = 14;

function zigzag(g: Graphics, body: Body, inner: number, outer: number, count: number): void {
  const r = body.radius;
  for (let i = 0; i <= count * 2; i++) {
    const a = (i / (count * 2)) * TAU - Math.PI / 2;
    const rr = r * (i % 2 === 0 ? outer : inner);
    const x = body.x + Math.cos(a) * rr * body.scaleX;
    const y = body.y + Math.sin(a) * rr * body.scaleY;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    const cord = lerpColor(color, pal.ink, 0.5);
    g.ellipse(body.x, body.y, r * 0.64 * body.scaleX, r * 0.64 * body.scaleY).fill({ color: lerpColor(color, pal.rim, 0.5), alpha: 0.42 * alpha });
    if (r >= 7) {
      g.ellipse(body.x, body.y, r * 0.66 * body.scaleX, r * 0.66 * body.scaleY).stroke({ width: Math.max(1, r * 0.03), color: cord, alpha: 0.4 * alpha });
      zigzag(g, body, 0.7, 0.93, ROPE_V);
      g.stroke({ width: Math.max(1, r * 0.045), color: cord, alpha: 0.55 * alpha, join: 'round' });
    }
    k.highlight(body, 0.5 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: fill, alpha });
    g.ellipse(body.x, body.y, r * 0.52 * body.scaleX, r * 0.52 * body.scaleY).fill({ color: pal.heldLight, alpha: 0.4 * alpha });
    g.ellipse(body.x, body.y, r * 0.86 * body.scaleX, r * 0.86 * body.scaleY);
    g.stroke({ width: r * 0.2, color: lerpColor(fill, pal.heldShade, 0.12), alpha });
    zigzag(g, body, 0.77, 0.95, ROPE_V);
    g.stroke({ width: Math.max(1.2, r * 0.06), color: lerpColor(pal.spot, pal.heldLight, 0.72), alpha, join: 'round' });
    k.ringDots(body, r * 0.95, ROPE_V, -Math.PI / 2, (x, y) => {
      g.circle(x, (y - body.y) * body.scaleY + body.y, Math.max(1, r * 0.045));
    });
    g.fill({ color: lerpColor(pal.spot, pal.heldLight, 0.6), alpha });
    k.highlight(body, 0.45 * alpha);
    k.trace(body);
    g.stroke({ width, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.1 * alpha });
    g.circle(body.x, body.y, reach).stroke({ width: Math.max(1, edge * 0.45), color: pal.spot, alpha: 0.32 * alpha });
    const s = Math.max(2.6, k.unit * 0.014);
    k.ringDots(body, reach, KNOTS, spin, (x, y, _i, a) => {
      const tx = -Math.sin(a) * s * 0.5;
      const ty = Math.cos(a) * s * 0.5;
      g.circle(x + tx, y + ty, s * 0.6);
      g.circle(x - tx, y - ty, s * 0.6);
    });
    g.stroke({ width: Math.max(1, s * 0.34), color: lerpColor(pal.spot, pal.rim, 0.25), alpha: 0.85 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.8 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.spot : pal.held;
    if (on) g.circle(x, y, radius * 2).fill({ color: tone, alpha: 0.2 * alpha });
    g.circle(x, y, radius).fill({ color: on ? lerpColor(tone, pal.rim, 0.2) : lerpColor(pal.bg, pal.rim, 0.16), alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
    if (radius < 3) return;
    g.moveTo(x - radius * 0.22, y - radius * 0.5)
      .lineTo(x + radius * 0.22, y - radius * 0.5)
      .lineTo(x + radius * 0.48, y + radius * 0.38)
      .lineTo(x - radius * 0.48, y + radius * 0.38)
      .closePath();
    g.fill({ color: on ? lerpColor(tone, pal.ink, 0.5) : lerpColor(pal.bg, pal.rim, 0.32), alpha: 0.8 * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const rope = Math.max(1.6, k.unit * 0.0075);
    g.moveTo(points[0]!, points[1]!);
    for (let i = 2; i < count; i += 2) g.lineTo(points[i]!, points[i + 1]!);
    g.stroke({ width: rope, color: pal.path, alpha: alpha * 2.2, cap: 'round', join: 'round' });
    const step = Math.max(5, rope * 2.6);
    let left = step;
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const len = Math.hypot(dx, dy);
      if (len <= 0) continue;
      const ux = dx / len;
      const uy = dy / len;
      const tx = (ux - uy) * 0.707 * rope * 0.6;
      const ty = (uy + ux) * 0.707 * rope * 0.6;
      let d = left;
      for (; d < len; d += step) {
        const px = x0 + ux * d;
        const py = y0 + uy * d;
        g.moveTo(px - tx, py - ty).lineTo(px + tx, py + ty);
      }
      left = d - len;
    }
    g.stroke({ width: Math.max(1, rope * 0.45), color: pal.bg, alpha: alpha * 1.6, cap: 'butt' });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 2) {
      const c = Math.cos(rot);
      const s = Math.sin(rot);
      const lx = c * size * 1.5;
      const ly = s * size * 1.5;
      const wx = -s * size * 0.26;
      const wy = c * size * 0.26;
      g.poly([x - lx - wx, y - ly - wy, x + lx - wx, y + ly - wy, x + lx + wx, y + ly + wy, x - lx + wx, y - ly + wy], true);
    } else g.circle(x, y, size * 0.85);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const gold = pal.held;
    g.circle(m.x, m.y, m.r * 0.94).stroke({ width: m.width * (1 - 0.5 * m.grow), color: gold, alpha: m.fade * 0.55 });
    const ring = m.r * 1.2;
    const dot = Math.max(1.5, m.unit * 0.009);
    for (let i = 0; i < SEQUINS; i++) {
      const a = (i / SEQUINS) * TAU + m.grow * 0.5;
      g.circle(m.x + Math.cos(a) * ring, m.y + Math.sin(a) * ring, dot).fill({ color: i % 2 === 0 ? gold : pal.spot, alpha: m.fade * 0.85 });
    }
    const cw = m.unit * 0.06;
    const ch = m.unit * 0.04;
    const cx = m.x;
    const cy = m.y - ring - m.unit * (0.03 + 0.08 * m.grow);
    g.poly([cx - cw, cy, cx - cw, cy - ch, cx - cw * 0.5, cy - ch * 0.45, cx, cy - ch * 1.2, cx + cw * 0.5, cy - ch * 0.45, cx + cw, cy - ch, cx + cw, cy], true);
    g.fill({ color: gold, alpha: m.fade * 0.45 });
    g.stroke({ width: Math.max(1, m.width * 0.6), color: gold, alpha: m.fade * 0.9, join: 'round' });
    for (const [dx, dy] of [
      [-cw, -ch],
      [0, -ch * 1.2],
      [cw, -ch],
    ] as const) {
      g.circle(cx + dx, cy + dy, dot).fill({ color: pal.spot, alpha: m.fade * 0.85 });
    }
  },

  trail(g, x, y, size) {
    g.circle(x, y, size * 0.75);
  },
};
